import { toast } from 'sonner';

import {
  trackAdPlatformConnected,
  type AdAccountPlatform,
  type AdPlatformConversion,
} from '@/lib/analytics';
import { type ConnectionsSearch } from '@/components/connectors/connections-search';
import { isOAuthPopup } from '@/components/connectors/use-oauth-callback';
import { Platform } from '@/types/generated/enums';
import {
  broadcastResult,
  MSG_TYPE,
  OAUTH_CHANNEL,
  startedHere,
  type OAuthMessage,
} from '@/components/connectors/oauth-message';

const POPUP_CLOSE_DELAY_MS = 400;

// The server hands an event id only to the link that won the organization's platform claim.
export const conversionFor = (
  platform: AdAccountPlatform,
  eventId: string | null | undefined,
): AdPlatformConversion | undefined =>
  eventId ? { platform, eventId } : undefined;

// A callback names its platform in the search; only a Google or Meta link is an ad platform's.
export const callbackConversion = ({
  platform,
  event_id,
}: ConnectionsSearch): AdPlatformConversion | undefined => {
  if (platform !== Platform.GOOGLE && platform !== Platform.META)
    return undefined;
  return conversionFor(platform, event_id);
};

interface AdAccountConnection {
  platform: AdAccountPlatform;
  eventId: string | null | undefined;
  connectedMessage: string;
  onConnected: () => void;
}

export const completeAdAccountConnection = ({
  platform,
  eventId,
  connectedMessage,
  onConnected,
}: AdAccountConnection): void => {
  const conversion = conversionFor(platform, eventId);

  if (isOAuthPopup()) {
    // A listening window fires it: this one closes too soon to trust a tag dispatched here.
    broadcastResult('success', null, conversion);
    setTimeout(() => window.close(), POPUP_CLOSE_DELAY_MS);
    return;
  }

  if (conversion) trackAdPlatformConnected(conversion);
  toast.success(connectedMessage);
  onConnected();
};

// Armed all session, not only while a popup is pending: the claim a slow picker won is spent.
export const listenForAdAccountConversions = (): void => {
  const handle = (data: OAuthMessage | undefined): void => {
    // The sender hears its own broadcast; the window that is about to close must not fire.
    if (isOAuthPopup()) return;
    if (data?.type !== MSG_TYPE || data.status !== 'success') return;
    // Every tab hears the broadcast; only the one that started the flow counts it.
    if (!startedHere(data)) return;
    if (data.conversion) trackAdPlatformConnected(data.conversion);
  };

  try {
    const channel = new BroadcastChannel(OAUTH_CHANNEL);
    channel.onmessage = (event) =>
      handle(event.data as OAuthMessage | undefined);
  } catch {
    // A browser without BroadcastChannel still reaches the opener below.
  }

  window.addEventListener('message', (event) => {
    if (event.origin !== window.location.origin) return;
    handle(event.data as OAuthMessage | undefined);
  });
};
